import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ArrowRight } from 'lucide-react';

export function PricingPage() {
  const plans = [
    {
      name: 'Starter',
      price: '$5',
      period: 'per employee/month',
      description: 'Perfect for growing companies',
      features: [
        'Core HR Module',
        'Attendance & Leave',
        'Employee Self-Service',
        'Mobile App Access',
        'Email Support',
        'Up to 100 employees'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Growth',
      price: '$8',
      period: 'per employee/month',
      description: 'Most popular for mid-sized companies',
      features: [
        'Everything in Starter',
        'Payroll Management',
        'Performance Management',
        'Recruitment / ATS',
        'Custom Reports',
        'API Access',
        'Priority Support',
        'Up to 500 employees'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'tailored pricing',
      description: 'For large organizations',
      features: [
        'Everything in Growth',
        'Talent Management',
        'Learning & Development',
        'AI HR Assistant',
        'Advanced Analytics',
        'Custom Integrations',
        'Dedicated Account Manager',
        'SLA Guarantee',
        'Unlimited employees'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <>
      <SEO
        title="Pricing - Nestor"
        description="Transparent pricing for Nestor. Starting at $5 per employee per month. Choose the plan that fits your organization."
      />

      <section className="bg-gradient-to-b from-primary/10 via-background to-background py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Simple, Transparent Pricing</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Pay only for what you use. No hidden fees. Cancel anytime.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <Card key={index} className={plan.popular ? 'border-primary shadow-lg' : ''}>
                {plan.popular && (
                  <div className="bg-primary text-primary-foreground text-center py-1 text-sm font-medium rounded-t-lg">
                    Most Popular
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.price !== 'Custom' && <span className="text-muted-foreground ml-2">/{plan.period}</span>}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full" variant={plan.popular ? 'default' : 'outline'} asChild>
                    <Link to={plan.cta === 'Contact Sales' ? '/contact' : '/demo'}>
                      {plan.cta}
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="mt-16 text-center max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Need a Custom Plan?</h3>
            <p className="text-muted-foreground mb-6">
              We offer flexible pricing for enterprises with unique requirements. Get in touch to discuss custom pricing and integrations.
            </p>
            <Button size="lg" asChild>
              <Link to="/contact">Contact Sales <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
